import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/cn.js";

/**
 * StatBar — pill-rounded progress bar for player stats (DESIGN.md "Progress Bars").
 * Use `tone` to switch between Pitch Lime (default), tactical Blue, or a custom tw color via className on the fill.
 */
export interface StatBarProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /** Current value in [0..max]. */
  value: number;
  max?: number;
  label?: string;
  tone?: "lime" | "blue" | "red" | "amber" | "violet";
  showValue?: boolean;
}

const toneFill: Record<NonNullable<StatBarProps["tone"]>, string> = {
  lime: "bg-pitch-lime",
  blue: "bg-tactical-blue",
  red: "bg-tactical-red",
  // Extended palette — known Tailwind colors that pair with the dark glass
  // theme and let the PlayersPage assign one distinctive hue per attribute.
  amber: "bg-amber-400",
  violet: "bg-violet-400",
};

export const StatBar = forwardRef<HTMLDivElement, StatBarProps>(function StatBar(
  { value, max = 100, label, tone = "lime", showValue = true, className, ...props },
  ref,
) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props}>
      {(label || showValue) && (
        <div className="flex items-center justify-between text-label-caps uppercase text-muted-foreground">
          {label ? <span>{label}</span> : <span aria-hidden />}
          {showValue && (
            <span className="tabular-nums text-foreground">
              {Math.round(value)}
              <span className="text-muted-foreground">/{max}</span>
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={label}
        className="relative h-2 w-full overflow-hidden rounded-full bg-[#2D2D2D]"
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-base ease-standard",
            toneFill[tone],
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
});
